import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { Check, Copy, Download, QrCode } from 'lucide-react';

// QR code d'une version de pièce (04/11, skill PCB) : le même sur tous les exemplaires de la version, pour la
// sérigraphie d'une carte ou la plaque d'une pièce. Lien court /plm/<pièce>/<version> (QR plus petit), que
// l'application renvoie vers la fiche. Le QR d'un exemplaire reste celui de son stock (étiquette).

export const versionLink = (itemId, versionNumber) => `${window.location.origin}/plm/${itemId}/${versionNumber}`;

const download = (href, filename) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.click();
};

const slug = (text) => (text || 'piece').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '') || 'piece';

const VersionQrCard = ({ item, versions, focusVersion }) => {
  const ordered = useMemo(() => [...(versions || [])].sort((a, b) => b.version_number - a.version_number), [versions]);
  const [number, setNumber] = useState(null);
  const [image, setImage] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const wanted = ordered.find((v) => String(v.version_number) === String(focusVersion));
    setNumber((wanted || ordered[0])?.version_number ?? null);
  }, [item?.id, focusVersion, ordered]);

  const url = number != null ? versionLink(item.id, number) : '';
  useEffect(() => {
    if (!url) { setImage(''); return undefined; }
    let active = true;
    QRCode.toDataURL(url, { margin: 2, width: 200, errorCorrectionLevel: 'M' })
      .then((data) => active && setImage(data)).catch(() => active && setImage(''));
    return () => { active = false; };
  }, [url]);

  if (!ordered.length) return null;
  const name = `QR_${slug(item.reference || item.name)}_V${number}`;
  const focused = focusVersion != null && String(focusVersion) === String(number);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* presse-papiers refusé : le lien reste affiché */ }
  };
  const svg = async () => {
    const text = await QRCode.toString(url, { type: 'svg', margin: 2, errorCorrectionLevel: 'M' });
    download(URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' })), `${name}.svg`);
  };

  return (
    <div className={`flex flex-wrap items-center gap-4 rounded-xl border p-3 ${focused ? 'border-indigo-300 bg-indigo-50/60' : 'border-slate-200'}`}>
      {image ? <img src={image} alt={`QR code de la version V${number}`} className="h-28 w-28 rounded bg-white" />
        : <div className="flex h-28 w-28 items-center justify-center rounded bg-slate-50"><QrCode className="h-8 w-8 text-slate-300" /></div>}
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">QR code de la version</span>
          <select value={number ?? ''} onChange={(e) => setNumber(Number(e.target.value))}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
            {ordered.map((v) => <option key={v.id} value={v.version_number}>V{v.version_number}{v.label ? ` · ${v.label}` : ''}</option>)}
          </select>
          {focused && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">ouverte par ce QR</span>}
        </div>
        <p className="text-xs text-slate-500">
          Le même sur toutes les pièces de cette version : sérigraphie d&apos;une carte, plaque d&apos;une pièce.
          Pour un exemplaire précis, prendre le QR de son stock.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="truncate rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{url}</code>
          <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} {copied ? 'Copié' : 'Copier le lien'}
          </button>
          <button type="button" onClick={svg} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <Download className="h-3.5 w-3.5" /> SVG
          </button>
          {image && (
            <button type="button" onClick={() => download(image, `${name}.png`)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> PNG
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VersionQrCard;
